import React, { useState } from 'react';

export interface RatingProps {
    value?: number;
    max?: number;
    size?: 'sm' | 'md' | 'lg';
    readonly?: boolean;
    allowHalf?: boolean;
    color?: 'yellow' | 'red' | 'blue' | 'green' | 'purple';
    className?: string;
    onChange?: (value: number) => void;
    onHover?: (value: number) => void;
    showValue?: boolean;
    label?: string;
    icon?: 'star' | 'heart' | 'thumb';
}

const Rating: React.FC<RatingProps> = ({
    value = 0,
    max = 5,
    size = 'md',
    readonly = false,
    allowHalf = false,
    color = 'yellow',
    className = '',
    onChange,
    onHover,
    showValue = false,
    label,
    icon = 'star',
}) => {
    const [hoverValue, setHoverValue] = useState<number | null>(null);
    const [isHovering, setIsHovering] = useState(false);

    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-6 h-6',
        lg: 'w-8 h-8',
    };

    const colorClasses = {
        yellow: {
            filled: 'text-yellow-400',
            empty: 'text-gray-300',
        },
        red: {
            filled: 'text-red-500',
            empty: 'text-gray-300',
        },
        blue: {
            filled: 'text-blue-500',
            empty: 'text-gray-300',
        },
        green: {
            filled: 'text-green-500',
            empty: 'text-gray-300',
        },
        purple: {
            filled: 'text-purple-500',
            empty: 'text-gray-300',
        },
    };

    const getIcon = (filled: boolean, half: boolean = false) => {
        const baseClasses = `${sizeClasses[size]} transition-colors duration-200`;
        const colorClass = filled || half
            ? colorClasses[color].filled
            : colorClasses[color].empty;

        switch (icon) {
            case 'heart':
                return (
                    <svg
                        className={`${baseClasses} ${colorClass}`}
                        fill={filled ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        />
                    </svg>
                );
            case 'thumb':
                return (
                    <svg
                        className={`${baseClasses} ${colorClass}`}
                        fill={filled ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                        />
                    </svg>
                );
            default: // star
                if (half) {
                    return (
                        <div className="relative">
                            <svg
                                className={`${baseClasses} ${colorClasses[color].empty}`}
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                            <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
                                <svg
                                    className={`${baseClasses} ${colorClasses[color].filled}`}
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                </svg>
                            </div>
                        </div>
                    );
                }
                return (
                    <svg
                        className={`${baseClasses} ${colorClass}`}
                        fill="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                );
        }
    };

    const handleClick = (index: number, isHalf: boolean = false) => {
        if (readonly) return;
        const newValue = isHalf ? index - 0.5 : index;
        onChange?.(newValue);
    };

    const handleMouseEnter = (index: number, isHalf: boolean = false) => {
        if (readonly) return;
        const newValue = isHalf ? index - 0.5 : index;
        setHoverValue(newValue);
        setIsHovering(true);
        onHover?.(newValue);
    };

    const handleMouseLeave = () => {
        if (readonly) return;
        setHoverValue(null);
        setIsHovering(false);
    };

    const currentValue = isHovering ? hoverValue! : value;

    const renderStars = () => {
        const stars = [];
        for (let i = 1; i <= max; i++) {
            const isFilled = i <= Math.floor(currentValue);
            const isHalf = allowHalf && i === Math.ceil(currentValue) && currentValue % 1 !== 0;

            stars.push(
                <div
                    key={i}
                    className={`relative ${readonly ? '' : 'cursor-pointer'}`}
                    onMouseLeave={handleMouseLeave}
                >
                    {allowHalf && !readonly ? (
                        <div className="flex">
                            <div
                                className="w-1/2 z-10 relative"
                                onClick={() => handleClick(i, true)}
                                onMouseEnter={() => handleMouseEnter(i, true)}
                            >
                                {getIcon(i <= Math.floor(currentValue), isHalf)}
                            </div>
                            <div
                                className="w-1/2 z-10 relative -ml-6"
                                onClick={() => handleClick(i)}
                                onMouseEnter={() => handleMouseEnter(i)}
                            >
                                {getIcon(isFilled, false)}
                            </div>
                        </div>
                    ) : (
                        <div
                            onClick={() => handleClick(i)}
                            onMouseEnter={() => handleMouseEnter(i)}
                        >
                            {getIcon(isFilled, isHalf)}
                        </div>
                    )}
                </div>
            );
        }
        return stars;
    };

    return (
        <div className={`inline-flex items-center gap-1 ${className}`}>
            {label && (
                <label className="text-sm font-medium text-gray-700 mr-2">
                    {label}
                </label>
            )}

            <div className="flex items-center gap-1">
                {renderStars()}
            </div>

            {showValue && (
                <span className="ml-2 text-sm text-gray-600">
                    {currentValue.toFixed(allowHalf ? 1 : 0)} / {max}
                </span>
            )}
        </div>
    );
};

export default Rating;